import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@trillion/ui/components/ui/alert-dialog";
import { toast } from "sonner";
import { api } from "../lib/api";
import type { Diagram, DiagramListItem, Team } from "../types/board";

// "all" | "mine" | "shared" | `team:<team_id>`
type Filter = string;
const TEAM_PREFIX = "team:";

function matches(diagram: DiagramListItem, filter: Filter): boolean {
  if (filter === "mine") return diagram.is_owner;
  if (filter === "shared") return !diagram.is_owner;
  if (filter.startsWith(TEAM_PREFIX)) return diagram.shared_via_team_ids.includes(filter.slice(TEAM_PREFIX.length));
  return true;
}

export function DashboardPage() {
  const [diagrams, setDiagrams] = useState<DiagramListItem[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [filter, setFilter] = useState<Filter>("all");
  const [leaving, setLeaving] = useState<DiagramListItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();

  const loadDiagrams = useCallback(
    () =>
      api<{ diagrams: DiagramListItem[] }>("/api/diagrams")
        .then((res) => setDiagrams(res.diagrams))
        .catch(() => setDiagrams([])),
    []
  );

  useEffect(() => {
    loadDiagrams().finally(() => setIsLoading(false));
    // A failed teams load leaves only the three fixed filter options.
    api<{ teams: Team[] }>("/api/teams")
      .then((res) => setTeams(res.teams))
      .catch(() => setTeams([]));
  }, [loadDiagrams]);

  async function handleCreate() {
    setIsCreating(true);
    try {
      const diagram = await api<Diagram>("/api/diagrams", {
        method: "POST",
        body: JSON.stringify({ title: "Untitled Board" }),
      });
      navigate(`/d/${diagram.diagram_id}`);
    } catch {
      // TODO: show error toast
    } finally {
      setIsCreating(false);
    }
  }

  async function handleLeave() {
    const target = leaving;
    setLeaving(null);
    if (!target) return;
    try {
      await api(`/api/diagrams/${target.diagram_id}/leave`, { method: "POST" });
      toast.success(`Left ${target.title}`);
    } catch {
      toast.error("Failed to leave diagram");
    }
    // Either way, reload: a diagram a team still reaches stays listed, now without Leave.
    await loadDiagrams();
  }

  const visible = diagrams.filter((diagram) => matches(diagram, filter));

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">Diagrams</h1>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger aria-label="Filter diagrams" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="mine">Mine</SelectItem>
              <SelectItem value="shared">Shared with me</SelectItem>
              {teams.map((team) => (
                <SelectItem key={team.team_id} value={`${TEAM_PREFIX}${team.team_id}`}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleCreate} disabled={isCreating}>
            {isCreating ? "Creating..." : "New Board"}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading diagrams...</p>
      ) : diagrams.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12">
          <p className="mb-4 text-sm text-muted-foreground">
            No diagrams yet. Create your first board to get started.
          </p>
          <Button onClick={handleCreate} disabled={isCreating}>
            New Board
          </Button>
        </div>
      ) : visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">No diagrams match this filter.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((diagram) => (
            <Card
              key={diagram.diagram_id}
              className="cursor-pointer transition-colors hover:bg-accent/50"
              onClick={() => navigate(`/d/${diagram.diagram_id}`)}
            >
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">{diagram.title}</CardTitle>
                  {!diagram.is_owner && diagram.shared_directly && (
                    <Button
                      variant="outline"
                      size="sm"
                      aria-label={`Leave ${diagram.title}`}
                      onClick={(event) => {
                        // Leaving must not also open the diagram.
                        event.stopPropagation();
                        setLeaving(diagram);
                      }}
                    >
                      Leave
                    </Button>
                  )}
                </div>
                <CardDescription>
                  Last modified {new Date(diagram.modified_date).toLocaleDateString()}
                </CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={leaving !== null} onOpenChange={(open) => !open && setLeaving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave {leaving?.title}?</AlertDialogTitle>
            <AlertDialogDescription>
              You will lose the access this diagram was shared with you directly. Access through a team stays.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleLeave}>Leave</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
